import Header from '../components/shared/Header'
import Footer from '../components/shared/Footer'
import MetaTags from '../components/shared/MetaTags'
import StructuredData from '../components/shared/StructuredData'

export default function AuthorPage() {
  return (
    <>
      <MetaTags
        title="Ryan Deeds | deedslab.com"
        description="Created by Ryan Deeds. Conducts experimental research at the intersection of strategy and innovation. Strategy is intent. Action is the test. Explore ongoing experiments and research methodology."
        url="/author"
        type="profile"
      />
      <StructuredData
        type="ProfilePage"
        title="Ryan Deeds | deedslab.com"
        description="Created by Ryan Deeds. Conducts experimental research at the intersection of strategy and innovation. Strategy is intent. Action is the test."
        url="/author"
        author={{
          name: "Ryan Deeds",
          jobTitle: "Creator",
          description: "Conducting experimental research in strategy, innovation, and complex systems analysis",
          sameAs: [
            "https://linkedin.com/in/ryandeeds",
            "https://twitter.com/ryandeeds",
            "https://deedslab.com"
          ]
        }}
      />

      <div className="min-h-screen bg-bg-dark text-text-dark">
        <div className="max-w-container mx-auto px-5 md:px-8 py-8">
          <Header />

          {/* Hero Section */}
          <section className="py-12 md:py-16">
            <div className="max-w-4xl mx-auto text-center">
              <div className="font-mono text-xs tracking-wide uppercase text-accent-dark mb-4">
                Principal Researcher
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-accent-dark to-accent-light bg-clip-text text-transparent">
                Ryan Deeds
              </h1>
              <p className="text-xl md:text-2xl text-muted-dark mb-4 font-light">
                Experimental Strategist
              </p>
              <p className="text-lg text-muted-dark leading-relaxed max-w-2xl mx-auto">
                <strong className="text-accent-dark">Strategy is intent. Action is the test.</strong> This laboratory explores innovation through controlled experimentation and real-world validation.
              </p>
            </div>
          </section>

          {/* Bio Section */}
          <section className="py-8 md:py-12">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-semibold mb-6 text-center">Research Statement</h2>
              <div className="prose prose-lg prose-invert mx-auto">
                <p className="text-base leading-relaxed mb-4">
                  Ryan Deeds is an Experimental Strategist exploring the intersection of strategy, technology, and human behavior through systematic testing.
                  The core approach: complex systems reveal their patterns not through speculation alone, but through controlled experimentation
                  and iterative validation in real-world conditions.
                </p>

                <p className="text-base leading-relaxed mb-4">
                  This digital laboratory maintains a comprehensive <strong className="text-text-dark">experimental record</strong>—a living archive of strategic tests
                  designed to validate assumptions, stress-test theories, and extract actionable insights from empirical evidence.
                  Each investigation represents a controlled study with clear variables, measurable outcomes, and documented observations.
                </p>

                <p className="text-base leading-relaxed mb-4">
                  The experimental methodology follows a rigorous protocol:
                  <strong className="text-text-dark"> Ideation → Hypothesis → Testing → Analysis → Iteration</strong>.
                  Every investigation begins with a strategic question, progresses through controlled testing,
                  and concludes with validated observations that inform subsequent explorations.
                </p>

                <div className="border border-border-dark rounded-lg p-4 mt-6 bg-surface-dark-1">
                  <p className="text-sm text-text-dark font-mono mb-2">CURRENT INVESTIGATION FOCUS</p>
                  <p className="text-sm text-muted-dark">
                    Exploring how strategic intent translates to measurable outcomes through experimental validation.
                    Current investigations examine decision-making frameworks, system dynamics, and innovation methodologies.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Active Experiments */}
          <section className="py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-semibold mb-8 text-center">Active Research Projects</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-border-dark rounded-large p-6 bg-surface-dark-1 hover:border-accent-dark/50 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-mono text-xs tracking-wide uppercase text-accent-dark bg-accent-dark/10 px-2 py-1 rounded">
                      Experiment 001
                    </span>
                    <span className="font-mono text-xs tracking-wide uppercase text-muted-dark bg-surface-dark-2 px-2 py-1 rounded">
                      Iterating
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-text-dark">Macro Insurance Pool Dynamics</h3>
                  <p className="text-sm text-muted-dark mb-4">
                    <strong className="text-accent-dark">Hypothesis:</strong> Insurance premium increases result from systemic pool dynamics rather than individual risk changes.
                    Testing interactive simulation of premium flows, claims events, and reinsurance mechanisms.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-dark font-mono">Initiated: Dec 2024</span>
                    <a href="/experiments/macro-insurance-simulator" className="text-accent-dark hover:underline text-sm font-medium">
                      Access Lab →
                    </a>
                  </div>
                </div>

                <div className="border border-border-dark rounded-large p-6 bg-surface-dark-1 hover:border-accent-dark/50 transition-colors">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-mono text-xs tracking-wide uppercase text-accent-dark bg-accent-dark/10 px-2 py-1 rounded">
                      Experiment 002
                    </span>
                    <span className="font-mono text-xs tracking-wide uppercase text-muted-dark bg-surface-dark-2 px-2 py-1 rounded">
                      Ideating
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-text-dark">Platform Meta-Analysis</h3>
                  <p className="text-sm text-muted-dark mb-4">
                    <strong className="text-accent-dark">Research Question:</strong> How does documenting the experimental process itself influence experimental design?
                    Meta-experiment tracking platform evolution and research methodology development.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-dark font-mono">Initiated: Dec 2024</span>
                    <a href="/experiments/building-this-site" className="text-accent-dark hover:underline text-sm font-medium">
                      View Logs →
                    </a>
                  </div>
                </div>
              </div>

              <div className="text-center mt-8 space-y-4">
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a href="/" className="inline-flex items-center gap-2 text-accent-dark hover:underline font-medium">
                    <span>View All Experiments</span>
                    <span className="text-sm">→</span>
                  </a>
                  <a href="/notes" className="inline-flex items-center gap-2 text-accent-dark hover:underline font-medium">
                    <span>Observations</span>
                    <span className="text-sm">→</span>
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Research Methodology */}
          <section className="py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-semibold mb-8 text-center">Research Methodology</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center border border-border-dark rounded-large p-6 bg-surface-dark-1">
                  <div className="w-12 h-12 bg-accent-dark rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sm font-bold text-black font-mono">01</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-accent-dark">Hypothesis Formation</h3>
                  <p className="text-sm text-muted-dark leading-relaxed">
                    Developing clear research questions and falsifiable hypotheses based on observed phenomena and theoretical frameworks.
                  </p>
                  <div className="mt-4 text-xs text-muted-dark font-mono uppercase tracking-wide">
                    Ideation Phase
                  </div>
                </div>

                <div className="text-center border border-border-dark rounded-large p-6 bg-surface-dark-1">
                  <div className="w-12 h-12 bg-accent-dark rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sm font-bold text-black font-mono">02</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-accent-dark">Controlled Testing</h3>
                  <p className="text-sm text-muted-dark leading-relaxed">
                    Designing and executing controlled experiments with measurable variables, systematic data collection, and iterative refinement.
                  </p>
                  <div className="mt-4 text-xs text-muted-dark font-mono uppercase tracking-wide">
                    Iteration Phase
                  </div>
                </div>

                <div className="text-center border border-border-dark rounded-large p-6 bg-surface-dark-1">
                  <div className="w-12 h-12 bg-accent-dark rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-sm font-bold text-black font-mono">03</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-3 text-accent-dark">Analysis & Synthesis</h3>
                  <p className="text-sm text-muted-dark leading-relaxed">
                    Statistical analysis of results, pattern identification, and synthesis of findings into actionable knowledge and future hypotheses.
                  </p>
                  <div className="mt-4 text-xs text-muted-dark font-mono uppercase tracking-wide">
                    Learning Phase
                  </div>
                </div>
              </div>

              <div className="mt-8 text-center">
                <div className="inline-block border border-accent-dark/30 rounded-lg p-4 bg-accent-dark/5 max-w-2xl">
                  <p className="text-sm text-accent-dark font-mono mb-2">EXPERIMENTAL ETHOS</p>
                  <p className="text-sm text-muted-dark">
                    Every experiment represents a controlled investigation into complex systems.
                    Success is measured not by confirmation of hypotheses, but by the quality and actionability of the learnings generated.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Research Collaboration */}
          <section className="py-8 md:py-12">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-semibold mb-6">Research Collaboration</h2>
              <p className="text-muted-dark mb-8">
                Interested in experimental research, strategic testing methodologies, or collaborative investigation opportunities?
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <a
                  href="https://linkedin.com/in/ryandeeds"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-4 border border-border-dark rounded-large bg-surface-dark-1 hover:border-accent-dark/50 transition-colors group"
                >
                  <svg className="w-6 h-6 text-[#0077b5] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  <span className="text-sm font-medium">LinkedIn</span>
                  <span className="text-xs text-muted-dark">Professional Network</span>
                </a>

                <a
                  href="https://twitter.com/ryandeeds"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-4 border border-border-dark rounded-large bg-surface-dark-1 hover:border-accent-dark/50 transition-colors group"
                >
                  <svg className="w-6 h-6 text-[#1da1f2] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                  <span className="text-sm font-medium">Twitter</span>
                  <span className="text-xs text-muted-dark">Research Updates</span>
                </a>

                <a
                  href="mailto:hello@deedslab.com"
                  className="flex flex-col items-center gap-2 p-4 border border-accent-dark rounded-large bg-accent-dark/5 hover:bg-accent-dark/10 transition-colors group"
                >
                  <svg className="w-6 h-6 text-accent-dark group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-medium">Contact</span>
                  <span className="text-xs text-muted-dark">Research Inquiry</span>
                </a>
              </div>

              <div className="border-t border-border-dark pt-6">
                <p className="text-sm text-muted-dark font-mono mb-4">CURRENT STATUS</p>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-dark/10 border border-accent-dark/30 rounded-full">
                  <div className="w-2 h-2 bg-accent-dark rounded-full animate-pulse"></div>
                  <span className="text-xs text-accent-dark font-medium">Actively Conducting Research</span>
                </div>
              </div>
            </div>
          </section>

          <Footer />
        </div>
      </div>
    </>
  )
}
